"use client";

import { createContext, useContext, useEffect, useState } from "react";

export type LLMPlatform = "chatgpt" | "claude" | "gemini" | "grok" | "unknown";

const LLM_NAMES: Record<LLMPlatform, string> = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  grok: "Grok",
  unknown: "",
};

const DEFAULT_PLATFORM: LLMPlatform = "unknown";
const PLATFORM_STORAGE_KEY = "passage-llm-platform";

interface LLMContextValue {
  platform: LLMPlatform;
  name: string;
}

const LLMContext = createContext<LLMContextValue>({
  platform: DEFAULT_PLATFORM,
  name: LLM_NAMES[DEFAULT_PLATFORM],
});

export function useLLM() {
  return useContext(LLMContext);
}

export function useLLMName(): string {
  return useLLM().name;
}

function isValidPlatform(value: string | null): value is LLMPlatform {
  return value !== null && value in LLM_NAMES;
}

function getPlatformFromUrl(): LLMPlatform | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  return isValidPlatform(platform) ? platform : null;
}

function getStoredPlatform(): LLMPlatform | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(PLATFORM_STORAGE_KEY);
    return isValidPlatform(stored) ? stored : null;
  } catch {
    return null;
  }
}

function storePlatform(platform: LLMPlatform): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PLATFORM_STORAGE_KEY, platform);
  } catch {
    // Ignore storage errors
  }
}

export default function LLMProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [platform, setPlatform] = useState<LLMPlatform>(() => {
    // Try URL first, then session storage, then default
    const urlPlatform = getPlatformFromUrl();
    if (urlPlatform) return urlPlatform;

    const storedPlatform = getStoredPlatform();
    if (storedPlatform) return storedPlatform;

    return DEFAULT_PLATFORM;
  });

  // On mount, check URL and persist to session storage
  useEffect(() => {
    const urlPlatform = getPlatformFromUrl();
    if (urlPlatform) {
      // eslint-disable-next-line
      setPlatform(urlPlatform);
      storePlatform(urlPlatform);
    } else {
      // If no URL param, check storage
      const storedPlatform = getStoredPlatform();
      if (storedPlatform) {
        setPlatform(storedPlatform);
      }
    }
  }, []);

  const value: LLMContextValue = {
    platform,
    name: LLM_NAMES[platform],
  };

  return <LLMContext.Provider value={value}>{children}</LLMContext.Provider>;
}
